import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
  forwardRef,
  ComponentPropsWithoutRef,
  ElementType,
  ReactNode,
  CSSProperties,
} from 'react';
import { usePrevious, useWindowSize } from 'react-use';
// use dirty version instead of of ordinary one to get rid of a infinite looping error
import useMeasureDirty from 'react-use/lib/useMeasureDirty';
import { Link, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import throttle from 'lodash/throttle';

import { compilePath } from '../../utils/helpers/routes';
import { heightChanged as wizardHeightChanged } from '../../store/wizard';
import useCombineRefs from '../../hooks/useCombineRefs';
import Icon from '../icon/Icon';
import CloseButton from '../close-button/CloseButton';
import { useMobile } from '../responsive/Responsive';

import './WizardLayout.scss';

const RESIZE_THROTTLE_TIMEOUT = 1000 / 30;

const MOBILE_RESIZABLE_FULL_TOP_OFFSET = 80;
const MOBILE_OUTER_BACK_BUTTON_VISIBILITY_TOP_OFFSET = 130;

const getMobileContentHeight = (stops, stopIndex, offset) =>
  Math.min(
    Math.max(stops[stopIndex] + offset, stops[0]),
    stops[stops.length - 1],
  );

const isMobileOuterBackButtonOverlapping = (stops, stopIndex, offset) => {
  if (!stops) {
    return false;
  }

  return (
    window.innerHeight - getMobileContentHeight(stops, stopIndex, offset) <
    MOBILE_OUTER_BACK_BUTTON_VISIBILITY_TOP_OFFSET
  );
};

export interface WizardLayoutProps
  extends ComponentPropsWithoutRef<ElementType> {
  className?: string;
  title;
  description;
  hasTitleBackButton;
  hasOuterBackButton;
  hasCloseButton;
  onCloseButtonClick;
  resizable;
  resizeOptions?: {
    minHeight?: number;
    maxHeight?: number;
  };
  fullScreen;
  shouldDispatchHeightChange?: boolean;
  children?: ReactNode;
}

const WizardLayout = (
  {
    className,
    title,
    description,
    hasTitleBackButton,
    hasOuterBackButton,
    hasCloseButton,
    onCloseButtonClick,
    resizable: outerResizable,
    resizeOptions: {
      minHeight: minResizeHeight,
      maxHeight: maxResizeHeight,
    } = {},
    fullScreen,
    shouldDispatchHeightChange = true,
    children,
    ...props
  }: WizardLayoutProps,
  outerLayoutRef,
) => {
  const history = useHistory();

  const [currentResizeStopIndex, setCurrentResizeStopIndex] =
    useState<number>();
  const dragStartYRef = useRef(null);
  const [dragOffset, setDragOffset] = useState<number | null>(null);

  const { height: windowHeight } = useWindowSize();

  const resizable = useMemo(
    () =>
      outerResizable &&
      ((minResizeHeight === undefined && maxResizeHeight === undefined) ||
        minResizeHeight !== maxResizeHeight),
    [outerResizable, minResizeHeight, maxResizeHeight],
  );

  const resizeStops = useMemo(() => {
    if (!resizable) {
      return;
    }

    const stops: number[] = [];

    const halfHeight = windowHeight / 2;
    const fullHeight = windowHeight - MOBILE_RESIZABLE_FULL_TOP_OFFSET;

    if (minResizeHeight !== undefined && minResizeHeight < halfHeight) {
      stops.push(minResizeHeight);
    }

    if (minResizeHeight !== undefined && minResizeHeight > halfHeight) {
      stops.push(minResizeHeight);
    } else if (maxResizeHeight !== undefined && maxResizeHeight < halfHeight) {
      stops.push(maxResizeHeight);
      return stops;
    } else {
      stops.push(halfHeight);
    }

    if (maxResizeHeight !== undefined && maxResizeHeight < fullHeight) {
      stops.push(maxResizeHeight);
    } else {
      stops.push(fullHeight);
    }

    return stops;
  }, [resizable, minResizeHeight, maxResizeHeight, windowHeight]);

  const prevResizeStops = usePrevious(resizeStops);
  useEffect(() => {
    if (resizeStops && resizeStops !== prevResizeStops) {
      // when resize stops are being changed, it means that it's needed to show uncollapsed wizard, even if it was collapsen on prev screen
      const baseHeight = windowHeight / 2;

      // get closest to half a screen stop
      let stopIndex;
      let stopOffset;

      resizeStops.forEach((stop, idx) => {
        const offset = Math.abs(stop - baseHeight);

        if (stopIndex === undefined || offset < stopOffset) {
          stopIndex = idx;
          stopOffset = offset;
        }
      });

      setCurrentResizeStopIndex(stopIndex);
    }
  }, [resizeStops, prevResizeStops, currentResizeStopIndex, windowHeight]);

  const handleDragStart = useCallback((e) => {
    setDragOffset(0);
    dragStartYRef.current = e.clientY || e.touches[0].clientY;
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDragMove = useCallback(
    throttle((e) => {
      if (dragStartYRef.current !== null) {
        setDragOffset(
          dragStartYRef.current - (e.clientY || e.touches[0].clientY),
        );
      }
    }, RESIZE_THROTTLE_TIMEOUT),
    [],
  );

  useEffect(() => {
    if (resizable) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('touchmove', handleDragMove);

      return () => {
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('touchmove', handleDragMove);
      };
    }
  }, [handleDragMove, resizable]);

  const handleDragEnd = useCallback(() => {
    if (!resizeStops) {
      return;
    }

    const height = getMobileContentHeight(
      resizeStops,
      currentResizeStopIndex,
      dragOffset,
    );

    let closestStopIndex;
    let closestStopOffset;

    resizeStops.forEach((stop, idx) => {
      const offset = Math.abs(height - stop);

      if (closestStopIndex === undefined || offset < closestStopOffset) {
        closestStopIndex = idx;
        closestStopOffset = offset;
      }
    });

    setCurrentResizeStopIndex(closestStopIndex);

    dragStartYRef.current = null;
    setDragOffset(null);
  }, [dragOffset, resizeStops, currentResizeStopIndex]);

  useEffect(() => {
    if (resizable) {
      document.addEventListener('mouseup', handleDragEnd);
      document.addEventListener('touchend', handleDragEnd);

      return () => {
        document.removeEventListener('mouseup', handleDragEnd);
        document.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [handleDragMove, handleDragEnd, resizable]);

  const innerRef = useRef<HTMLElement>(null);
  const { height: measuredHeight } = useMeasureDirty(innerRef);
  const [refCallback] = useCombineRefs<HTMLDivElement>(
    outerLayoutRef,
    innerRef,
  );

  const dispatch = useDispatch();
  const isMobile = useMobile();
  useLayoutEffect(() => {
    if (shouldDispatchHeightChange) {
      dispatch(wizardHeightChanged(isMobile ? measuredHeight : null));
    }
  }, [dispatch, measuredHeight, isMobile, shouldDispatchHeightChange]);

  const shouldRenderHeader = title || description || hasTitleBackButton;

  let style: CSSProperties | undefined = undefined;
  if (resizable && resizeStops && currentResizeStopIndex) {
    style = {
      height:
        dragOffset !== null
          ? getMobileContentHeight(
              resizeStops,
              currentResizeStopIndex,
              dragOffset,
            )
          : resizeStops[currentResizeStopIndex],
    };
  }

  return (
    <div
      className={classNames(
        'wizard-layout',
        {
          'wizard-layout_full-screen': fullScreen,
          'wizard-layout_resizable': resizable,
        },
        className,
      )}
      {...props}
      ref={refCallback}
      style={style}
    >
      {shouldRenderHeader && (
        <div
          className="wizard-layout__header"
          onTouchStart={resizable ? handleDragStart : undefined}
          onMouseDown={resizable ? handleDragStart : undefined}
        >
          {(title || hasTitleBackButton) && (
            <div className="wizard-layout__title-container">
              {hasTitleBackButton && (
                <button
                  className="wizard-layout__back-button"
                  type="button"
                  onClick={() => history.goBack()}
                >
                  <Icon
                    className="wizard-layout__back-button-icon"
                    name="arrow-left"
                  />
                </button>
              )}
              {title && <h1 className="wizard-layout__title">{title}</h1>}
            </div>
          )}
          {description && (
            <p className="wizard-layout__description">{description}</p>
          )}
        </div>
      )}
      {hasOuterBackButton &&
        !isMobileOuterBackButtonOverlapping(
          resizeStops,
          currentResizeStopIndex,
          dragOffset || 0,
        ) && (
          <button
            className="wizard-layout__outer-back-button"
            type="button"
            onClick={() => history.goBack()}
          >
            <Icon
              className="wizard-layout__back-button-icon"
              name="arrow-left"
            />
          </button>
        )}
      {resizable && !fullScreen && (
        <div
          className="wizard-layout__drag-handle"
          onTouchStart={shouldRenderHeader ? undefined : handleDragStart}
          onMouseDown={shouldRenderHeader ? undefined : handleDragStart}
        >
          <div className="wizard-layout__drag-handle-icon" />
        </div>
      )}
      {hasCloseButton &&
        (onCloseButtonClick ? (
          <CloseButton
            className="wizard-layout__close-button"
            type="button"
            onClick={onCloseButtonClick}
          />
        ) : (
          <CloseButton
            className="wizard-layout__close-button"
            component={Link}
            to={({ search }) => ({
              pathname: compilePath('root'),
              search: search,
            })}
          />
        ))}
      <div className="wizard-layout__content">{children}</div>
    </div>
  );
};

export default forwardRef(WizardLayout);
